import React, { useCallback, useEffect, useRef, useState } from "react";
import { getExtract, uploadToQdrant } from "../api/schemeApi";
import "./AdminPage.css";

const logoUrl = "/NIRUTHI-lOGO.png";

const navItems = [
    { name: "Uploads", link: "#uploads" },
];

const fileTypeBadges = [
    { label: ".pdf", variant: "error" },
    { label: ".docx", variant: "secondary" },
    { label: ".txt", variant: "surface" },
];

const AdminPage = ({ onBack }) => {
    const [stagedFiles, setStagedFiles] = useState([]);
    const [isDragActive, setIsDragActive] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activePage, setActivePage] = useState("Uploads");
    const [isProcessing, setIsProcessing] = useState(false);
    const [extractionResults, setExtractionResults] = useState(null);
    const [extractionError, setExtractionError] = useState("");
    const [collectionName, setCollectionName] = useState("");
    const [useSameCollection, setUseSameCollection] = useState(false);
    const [collectionNamesMap, setCollectionNamesMap] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [uploadSuccess, setUploadSuccess] = useState("");
    const [qdrantResponse, setQdrantResponse] = useState(null);
    const [uploadStates, setUploadStates] = useState({}); // per-index upload states
    const [showMoreMap, setShowMoreMap] = useState({}); // per-index see more/less
    const [collectionConflict, setCollectionConflict] = useState(null); // { index, collectionName }
    const [lastStagedFiles, setLastStagedFiles] = useState([]);
    const fileInputRef = useRef(null);

    const normalizeContentText = (contentItem) => {
        if (!contentItem) return "";
        if (typeof contentItem === "string") return contentItem;
        return contentItem.text || "";
    };

    const isPipeTableLine = (text) => {
        if (typeof text !== "string") return false;
        const trimmed = text.trim();
        return trimmed.startsWith("|") && trimmed.includes("|") && trimmed.split("|").length >= 3;
    };

    const isPipeTableDivider = (text) => {
        if (typeof text !== "string") return false;
        const trimmed = text.trim();
        return /^[|:\-\s]+$/.test(trimmed) && trimmed.includes("-");
    };

    const parsePipeTable = (lines) => {
        const rows = lines
            .map((line) => {
                const cells = line.trim().split("|").map((part) => part.trim());
                if (cells[0] === "") cells.shift();
                if (cells[cells.length - 1] === "") cells.pop();
                return cells;
            })
            .filter((row) => row.length > 0);

        if (!rows.length) return null;

        let header = rows[0];
        let body = rows.slice(1);

        if (body.length > 0 && isPipeTableDivider(body[0].join("|"))) {
            body = body.slice(1);
        }

        return { header, rows: body };
    };

    const buildStructuredBlocks = (content = []) => {
        const lines = content.map(normalizeContentText).filter((text) => text !== "");
        const blocks = [];
        let textBuffer = [];
        let tableBuffer = [];

        const flushText = () => {
            if (textBuffer.length) {
                blocks.push({ type: "text", lines: [...textBuffer] });
                textBuffer = [];
            }
        };

        const flushTable = () => {
            if (tableBuffer.length) {
                blocks.push({ type: "table", lines: [...tableBuffer] });
                tableBuffer = [];
            }
        };

        for (const line of lines) {
            if (isPipeTableLine(line)) {
                flushText();
                tableBuffer.push(line);
            } else if (tableBuffer.length > 0 && line.trim().startsWith("|")) {
                tableBuffer.push(line);
            } else {
                flushTable();
                textBuffer.push(line);
            }
        }

        flushTable();
        flushText();

        return blocks;
    };

    const renderStructuredBlocks = (content = []) => {
        const blocks = buildStructuredBlocks(content);
        return blocks.map((block, blockIndex) => {
            if (block.type === "table") {
                const table = parsePipeTable(block.lines);
                if (!table || !table.header.length) {
                    return block.lines.map((line, lineIndex) => (
                        <p key={`line-${blockIndex}-${lineIndex}`}>{line}</p>
                    ));
                }

                return (
                    <div className="structured-pipe-table" key={`table-${blockIndex}`}>
                        <table>
                            <thead>
                                <tr>
                                    {table.header.map((cell, cellIndex) => (
                                        <th key={`header-${blockIndex}-${cellIndex}`}>{cell}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {table.rows.map((row, rowIndex) => (
                                    <tr key={`row-${blockIndex}-${rowIndex}`}>
                                        {row.map((cell, cellIndex) => (
                                            <td key={`cell-${blockIndex}-${rowIndex}-${cellIndex}`}>{cell}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            }

            return block.lines.map((line, lineIndex) => (
                <p key={`text-${blockIndex}-${lineIndex}`}>{line}</p>
            ));
        });
    };

    const handleBack = () => {
        if (onBack) onBack();
        else if (window.history.length > 1) window.history.back();
        else window.location.href = "/";
    };

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 32);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleFiles = useCallback((files) => {
        const fileList = Array.from(files || []);
        if (!fileList.length) return;
        setStagedFiles(fileList);
        setIsDragActive(false);
    }, []);

    const handleFileChange = (event) => {
        handleFiles(event.target.files);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
        handleFiles(event.dataTransfer.files);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
    };

    const handleClickDropZone = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (!bytes) return "0 B";
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    const handleRemoveFile = (index) => {
        setStagedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleStartExtraction = async () => {
        if (!stagedFiles.length) {
            setExtractionError("No files staged for extraction.");
            return;
        }

        setIsProcessing(true);
        setExtractionError("");
        setExtractionResults(null);
        setCollectionName("");
        setUseSameCollection(false);
        setUploadError("");
        setUploadSuccess("");
        setQdrantResponse(null);

        try {
            // remember files we sent for this extraction so we can reference them later
            setLastStagedFiles(stagedFiles);

            let responses = null;

            if (stagedFiles.length > 1) {
                // upload files individually to ensure backend processes each file
                const promises = stagedFiles.map((file) => getExtract([file]));
                responses = await Promise.all(promises);
            } else {
                // single file (or single-item array) - preserve existing behaviour
                responses = await getExtract(stagedFiles);
            }

            // normalize responses: if multiple, store array, else store object
            if (Array.isArray(responses)) {
                const anyError = responses.find((r) => r && r.error);
                if (anyError) {
                    setExtractionError(anyError.error || "Extraction failed for one or more files.");
                    return;
                }
                setExtractionResults(responses);
                // initialize per-file collection name inputs and upload states
                const map = Object.fromEntries(responses.map((r, i) => [i, ""]));
                setCollectionNamesMap(map);
                const upStates = Object.fromEntries(responses.map((r, i) => [i, { isUploading: false, uploadError: "", uploadSuccess: "", qdrantResponse: null }]));
                setUploadStates(upStates);
                const showMap = Object.fromEntries(responses.map((r, i) => [i, false]));
                setShowMoreMap(showMap);
            } else {
                if (responses && responses.error) {
                    setExtractionError(responses.error);
                    return;
                }
                setExtractionResults(responses);
                setCollectionNamesMap({ 0: "" });
                setUploadStates({ 0: { isUploading: false, uploadError: "", uploadSuccess: "", qdrantResponse: null } });
                setShowMoreMap({ 0: false });
            }

            setStagedFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = null;
            }
        } catch (error) {
            setExtractionError(
                error.message || "Failed to extract file. Please try again."
            );
            console.error("Extraction error:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    const renderSingleExtractionResult = (extractionResults, index) => {
        const result = extractionResults;
        return (
            <section className="extraction-result success-result" key={`extraction-${index}`}>
                <div className="result-header success">
                    <span className="material-symbols-outlined">check_circle</span>
                    <h3>Extraction Successful</h3>
                </div>

                <div className="result-content">
                    <div className="result-section">
                        <h4>File Information</h4>
                        <table className="result-table">
                            <tbody>
                                <tr>
                                    <th>Filename</th>
                                    <td>{result.filename}</td>
                                </tr>
                                <tr>
                                    <th>File Type</th>
                                    <td>{result.file_type}</td>
                                </tr>
                                <tr>
                                    <th>Pipeline Used</th>
                                    <td>{result.pipeline_used}</td>
                                </tr>
                                <tr>
                                    <th>Extraction Time</th>
                                    <td>{result.extraction_time_seconds}s</td>
                                </tr>
                                <tr>
                                    <th>Saved Location</th>
                                    <td>{result.saved_folder}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {result.quality && (
                        <div className="result-section">
                            <h4>Quality Metrics</h4>
                            <table className="score-table">
                                <tbody>
                                    <tr>
                                        <th>Overall Score</th>
                                        <td>{(result.quality.overall_score * 100).toFixed(2)}%</td>
                                    </tr>
                                    {result.quality.scores && Object.entries(result.quality.scores).map(([key, value]) => (
                                        <tr key={key}>
                                            <th>{key.replace(/_/g, " ")}</th>
                                            <td>{(value * 100).toFixed(2)}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {result.structured_data && (
                        <div className="result-section">
                            <h4>Extracted Content</h4>
                            {result.structured_data.title && (
                                <p><strong>Title:</strong> {result.structured_data.title}</p>
                            )}
                            {result.structured_data.sections && result.structured_data.sections.length > 0 && (
                                <>
                                    <table className="structured-table">
                                        <thead>
                                            <tr>
                                                <th>Section</th>
                                                <th>Content</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const collapsed = !showMoreMap[index];
                                                const sectionsToShow = collapsed ? result.structured_data.sections.slice(0, 2) : result.structured_data.sections;
                                                return sectionsToShow.map((section, idx) => {
                                                    const content = Array.isArray(section.content) ? section.content : [];
                                                    const previewContent = (!collapsed && content) ? content : content.slice(0, 6);
                                                    return (
                                                        <tr key={idx}>
                                                            <td>{section.section_name || `Section ${idx + 1}`}</td>
                                                            <td>
                                                                <div className="section-content-brief">
                                                                    {renderStructuredBlocks(previewContent)}
                                                                    {collapsed && content.length > previewContent.length && (
                                                                        <div className="preview-note">... (truncated)</div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })()}
                                        </tbody>
                                    </table>

                                    {result.structured_data.sections.length > 2 && (
                                        <div style={{ marginTop: 8 }}>
                                            <button
                                                type="button"
                                                className="see-more-btn"
                                                onClick={() => setShowMoreMap((prev) => ({ ...prev, [index]: !prev[index] }))}
                                            >
                                                {showMoreMap[index] ? "See less" : "See more"}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="result-section qdrant-upload-section">
                        <h4>Upload to Qdrant</h4>
                        <p>{useSameCollection ? "This document will use the shared collection name above." : "Enter a collection name and upload the extracted JSON to Qdrant."}</p>
                        {!useSameCollection && (
                            <div className="form-field">
                                <label className="studio-label" htmlFor={`collectionName-${index}`}>
                                    Collection Name
                                </label>
                                <input
                                    id={`collectionName-${index}`}
                                    type="text"
                                    className="studio-textarea"
                                    placeholder="Enter Qdrant collection name"
                                    value={collectionNamesMap[index] ?? ""}
                                    onChange={(e) => setCollectionNamesMap((prev) => ({ ...prev, [index]: e.target.value }))}
                                    disabled={isUploading}
                                    data-collection-index={index}
                                />
                            </div>
                        )}
                        <button
                            type="button"
                            className="extract-button"
                            onClick={() => handleUploadToQdrant(result, index)}
                            disabled={(uploadStates[index]?.isUploading) || !(useSameCollection ? collectionName.trim() : collectionNamesMap[index]?.trim())}
                        >
                            {uploadStates[index]?.isUploading ? "Uploading..." : "Upload to Qdrant"}
                        </button>
                                {(() => {
                                    const st = uploadStates[index] || {};
                                    return (
                                        <>
                                            {st.uploadError && (
                                                <p className="error-message" style={{ marginTop: "12px" }}>
                                                    {st.uploadError}
                                                </p>
                                            )}
                                            {st.uploadSuccess && (
                                                <div className="result-notice" style={{ marginTop: "12px" }}>
                                                    <p>{st.uploadSuccess}</p>
                                                    {st.qdrantResponse && (
                                                        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                                            {JSON.stringify(st.qdrantResponse, null, 2)}
                                                        </pre>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                    </div>
                </div>

                <button
                    type="button"
                    className="extract-button"
                    onClick={() => setExtractionResults(null)}
                >
                    Close Results
                </button>
            </section>
        );
    };

    const handleUploadToQdrant = async (result, index, forceOverwrite = false) => {
        const target = result || extractionResults;
        const collName = useSameCollection ? collectionName.trim() : ((typeof index !== 'undefined' && collectionNamesMap[index]) ? collectionNamesMap[index].trim() : "");
        if (!collName || !target) {
            setUploadStates((prev) => ({
                ...prev,
                [index ?? 0]: { isUploading: false, uploadError: "Please enter a collection name.", uploadSuccess: "", qdrantResponse: null },
            }));
            return;
        }

        // set per-file uploading state
        setUploadStates((prev) => ({
            ...prev,
            [index ?? 0]: { isUploading: true, uploadError: "", uploadSuccess: "", qdrantResponse: null },
        }));
        setIsUploading(true);

        try {
            const payload = {
                filename: (target && target.filename) || "extracted_file.json",
                collection_name: collName,
                structured_data: target,
                input_files: (lastStagedFiles || []).map((f) => ({ filename: f.name, size: f.size })),
                force_push_existing: forceOverwrite,
                create_if_missing: true,
            };
            const response = await uploadToQdrant(payload);

            if (response.error) {
                // Check if it's a 409 conflict (collection already exists)
                if (response.status === 409) {
                    setUploadStates((prev) => ({
                        ...prev,
                        [index ?? 0]: { isUploading: false, uploadError: "", uploadSuccess: "", qdrantResponse: null },
                    }));
                    setCollectionConflict({ index, collectionName: collName });
                    return;
                }
                setUploadStates((prev) => ({
                    ...prev,
                    [index ?? 0]: { isUploading: false, uploadError: response.error, uploadSuccess: "", qdrantResponse: null },
                }));
                return;
            }

            setUploadStates((prev) => ({
                ...prev,
                [index ?? 0]: { isUploading: false, uploadError: "", uploadSuccess: "Extraction results uploaded to Qdrant successfully.", qdrantResponse: response },
            }));
            // also set global last response for backwards compatibility
            setUploadSuccess("Extraction results uploaded to Qdrant successfully.");
            setQdrantResponse(response);
            setCollectionConflict(null);
        } catch (error) {
            const errMsg = error.message || "Failed to upload to Qdrant. Please try again.";
            setUploadStates((prev) => ({
                ...prev,
                [index ?? 0]: { isUploading: false, uploadError: errMsg, uploadSuccess: "", qdrantResponse: null },
            }));
            console.error("Qdrant upload error:", error);
        } finally {
            setIsUploading(false);
        }
    };

    React.useEffect(() => {
        if (activePage === "Uploads" && typeof window !== "undefined" && window.history) {
            window.history.replaceState({}, "", "/admin_uploads");
        }
    }, [activePage]);

    return (
        <div className="admin-page">
            <nav className={`resizable-navbar ${isScrolled ? "scrolled" : ""}`}>
                <div className="navbar-body">
                    <button type="button" className="admin-back-button" onClick={handleBack}>
                        <span className="material-symbols-outlined">arrow_back</span>
                        Back
                    </button>

                    <div className="navbar-logo">
                        <img src={logoUrl} alt="Niruthi Logo" className="logo-img" />
                    </div>

                    <div className="navbar-center">
                        <div className="navbar-title">Admin Portal</div>
                        <div className="navbar-subtitle">Manage uploads, view logs, and configure system settings</div>
                    </div>

                    <div className="navbar-right">
                        <div className="nav-items hide-mobile">
                            {navItems.map((item) => (
                                <a
                                    key={item.name}
                                    href={item.link}
                                    className={`nav-link ${item.name === activePage ? "active" : ""}`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setActivePage(item.name);
                                        if (item.name === "Uploads" && typeof window !== "undefined" && window.history) {
                                            window.history.pushState({}, "", "/admin_uploads");
                                        }
                                        setIsMobileMenuOpen(false);
                                    }}
                                >
                                    {item.name}
                                </a>
                            ))}
                        </div>

                        {isMobileMenuOpen ? (
                            <button
                                className="mobile-nav-close show-mobile"
                                type="button"
                                onClick={() => setIsMobileMenuOpen(false)}
                                aria-label="Close mobile menu"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        ) : (
                            <button
                                className="mobile-nav-toggle show-mobile"
                                type="button"
                                onClick={() => setIsMobileMenuOpen((value) => !value)}
                                aria-label="Toggle mobile menu"
                            >
                                <span className="material-symbols-outlined">menu</span>
                            </button>
                        )}
                    </div>
                </div>

                {isMobileMenuOpen && (
                    <div className="mobile-nav-menu">
                        <div className="mobile-nav-links">
                            {navItems.map((item) => (
                                <a
                                    key={item.name}
                                    href={item.link}
                                    className={`mobile-nav-link ${item.name === activePage ? "active" : ""}`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setActivePage(item.name);
                                        setIsMobileMenuOpen(false);
                                    }}
                                >
                                    {item.name}
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </nav>

            <main className="admin-main" id="admin-main">
                <div className="admin-content">
                    <section className="hero-section">
                        <div className="hero-text">
                            <h1>{activePage === "Uploads" ? "Upload & Extract" : activePage}</h1>
                            <p>
                                {activePage === "Uploads"
                                    ? "Efficiently ingest agricultural reports, soil analysis sheets, and harvest logs. Upload documents to run automated extraction sequences. Max file size: 20MB."
                                    : activePage === "Dashboard"
                                    ? "Monitor upload performance, pipeline status, and extraction metrics from a single dashboard."
                                    : "Review document logs, extraction history, and system events from the admin log center."}
                            </p>
                        </div>
                    </section>

                    {activePage === "Uploads" ? (
                        <section className="dashboard-grid" id="uploads">
                            <section
                                className={`upload-card ${isDragActive ? "drop-zone-active" : ""}`}
                                onClick={handleClickDropZone}
                                onDragEnter={handleDragOver}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                role="button"
                                tabIndex={0}
                            >
                                <div className="upload-icon">
                                    <span className="material-symbols-outlined">cloud_upload</span>
                                </div>
                                {stagedFiles.length > 0 ? (
                                    <div className="upload-staged">
                                        <span className="material-symbols-outlined staged-icon">task_alt</span>
                                        <p className="staged-title">{stagedFiles.length} File(s) Staged</p>
                                        <p className="staged-subtitle">Preparing for extraction engine...</p>
                                        <ul className="staged-file-list">
                                            {stagedFiles.map((f, idx) => (
                                                <li key={idx} className="staged-file-item">
                                                    <span className="file-name">{f.name}</span>
                                                    <span className="file-size">{formatBytes(f.size)}</span>
                                                    <button
                                                        type="button"
                                                        className="remove-file-btn"
                                                        onClick={(e) => { e.stopPropagation(); handleRemoveFile(idx); }}
                                                        aria-label={`Remove ${f.name}`}
                                                    >
                                                        Remove
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <>
                                        <h3>Drop your documents here</h3>
                                        <p className="upload-description">
                                            or <button type="button" className="upload-browse">click to browse</button> from your local storage
                                        </p>
                                        <div className="file-types-row">
                                            {fileTypeBadges.map((badge) => (
                                                <div key={badge.label} className={`file-type-badge ${badge.variant}`}>
                                                    <span>{badge.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                                <input
                                    ref={fileInputRef}
                                    className="hidden-input"
                                    type="file"
                                    accept=".pdf,.docx,.txt"
                                    multiple
                                    onChange={handleFileChange}
                                />
                            </section>

                            {stagedFiles.length > 0 && (
                                <section className="extraction-action-panel">
                                    <button
                                        type="button"
                                        className="extract-button"
                                        onClick={handleStartExtraction}
                                        disabled={isProcessing}
                                    >
                                        {isProcessing ? "Processing..." : "Start Extraction"}
                                    </button>
                                </section>
                            )}

                            {extractionError && (
                                <section className="extraction-result error-result">
                                    <div className="result-header error">
                                        <span className="material-symbols-outlined">error</span>
                                        <h3>Extraction Failed</h3>
                                    </div>
                                    <p className="error-message">{extractionError}</p>
                                    <button
                                        type="button"
                                        className="extract-button"
                                        onClick={() => setExtractionError("")}
                                    >
                                        Dismiss
                                    </button>
                                </section>
                            )}

                            {extractionResults && Array.isArray(extractionResults) && extractionResults.length > 1 && (
                                <section className="collection-toggle-section">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={useSameCollection}
                                            onChange={(e) => setUseSameCollection(e.target.checked)}
                                        />
                                        Use same collection for all documents
                                    </label>
                                    {useSameCollection && (
                                        <div className="form-field">
                                            <label className="studio-label" htmlFor="sharedCollectionName">
                                                Shared Collection Name
                                            </label>
                                            <input
                                                id="sharedCollectionName"
                                                type="text"
                                                className="studio-textarea"
                                                placeholder="Enter shared Qdrant collection name"
                                                value={collectionName}
                                                onChange={(e) => setCollectionName(e.target.value)}
                                                disabled={isUploading}
                                            />
                                        </div>
                                    )}
                                </section>
                            )}
                            {extractionResults && (
                                Array.isArray(extractionResults)
                                    ? extractionResults.map((res, i) => renderSingleExtractionResult(res, i))
                                    : renderSingleExtractionResult(extractionResults, 0)
                            )}
                        </section>
                    ) : (
                        <section className="dashboard-grid" id={activePage.toLowerCase()}>
                            <div className="page-placeholder">
                                <h3>{activePage}</h3>
                                <p>
                                    {activePage === "Dashboard"
                                        ? "This area will display an overview of recent uploads, extraction status, and document analytics."
                                        : "This area will display log entries, processing details, and audit history for uploaded documents."}
                                </p>
                            </div>
                        </section>
                    )}
                </div>
            </main>

            {/* Collection Conflict Dialog */}
            {collectionConflict && (
                <div className="modal-overlay" onClick={() => setCollectionConflict(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Collection Already Exists</h3>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setCollectionConflict(null)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>
                                The collection <strong>"{collectionConflict.collectionName}"</strong> already exists in Qdrant.
                            </p>
                            <p>What would you like to do?</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="modal-btn modal-btn-primary"
                                onClick={() => {
                                    handleUploadToQdrant(
                                        Array.isArray(extractionResults)
                                            ? extractionResults[collectionConflict.index]
                                            : extractionResults,
                                        collectionConflict.index,
                                        true  // forceOverwrite = true
                                    );
                                    setCollectionConflict(null);
                                }}
                            >
                                Push Into This Collection
                            </button>
                            <button
                                type="button"
                                className="modal-btn modal-btn-secondary"
                                onClick={() => {
                                    setCollectionConflict(null);
                                    // Focus the collection name input so user can change it
                                    setTimeout(() => {
                                        const input = document.querySelector(
                                            `input[data-collection-index="${collectionConflict.index}"]`
                                        );
                                        if (input) input.focus();
                                    }, 0);
                                }}
                            >
                                Use Different Name
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
